import { Component, computed, input } from '@angular/core';
import { InnogyComponentRendering } from '@core/jss-models';
import { LinkListSectionComponent } from '@content/ui/link-list';
import { LinkField, TextField, type RichTextField } from '@core/jss-proxy';
import {
  DEFAULT_LINK_FIELD,
  DEFAULT_TEXT_FIELD,
  getField,
} from '@core/jss-utils';
import {
  computedContentSectionId,
  computedContentSectionTheme,
  ContentSectionComponent,
} from '@core/content-section';

const TWO_LINK_LIST_SECTION = 2;
const THREE_LINK_LIST_SECTION = 3;
const SIX_LINK_LIST_SECTION = 6;
const MIN_NUMBER_OF_LINKS = 1;
const MAX_NUMBER_OF_LINKS = 5;

@Component({
  selector: 'wl-link-list-container',
  templateUrl: './link-list.container.component.html',
  standalone: true,
  imports: [LinkListSectionComponent, ContentSectionComponent],
})
export class LinkListContainerComponent {
  readonly rendering = input.required<InnogyComponentRendering>();
  readonly id = computedContentSectionId(this.rendering);
  readonly theme = computedContentSectionTheme(this.rendering);
  readonly fields = computed(() => this.rendering().fields);
  readonly title = computed(() => getField<TextField>(this.fields(), 'Title'));
  readonly introduction = computed(() =>
    getField<RichTextField>(this.fields(), 'Introduction')
  );

  readonly numberOfLinkLists = computed<number>(() => {
    switch (this.rendering()?.componentName) {
      case 'LinkList2Section':
        return TWO_LINK_LIST_SECTION;
      case 'LinkList3Section':
        return THREE_LINK_LIST_SECTION;
      case 'LinkList6Section':
        return SIX_LINK_LIST_SECTION;
      default:
        return TWO_LINK_LIST_SECTION;
    }
  });

  readonly linkLists = computed(() => {
    const linkLists = [];
    for (let i = 1; i <= this.numberOfLinkLists(); i++) {
      linkLists.push(this.getLinkList(i));
    }
    return linkLists;
  });

  getLinkList(index: number) {
    const id = `LinkList${index}Title`;

    return {
      id,
      title: getField<TextField>(this.fields(), id, DEFAULT_TEXT_FIELD),
      links: this.getLinks(index),
    };
  }

  getLinks(index: number) {
    const links = [];
    for (
      let linkIndex = MIN_NUMBER_OF_LINKS;
      linkIndex <= MAX_NUMBER_OF_LINKS;
      linkIndex++
    ) {
      const id = `LinkList${index}Link${linkIndex}`;

      links.push({
        id,
        value: getField<LinkField>(this.fields(), id, DEFAULT_LINK_FIELD),
      });
    }

    return links.filter((link) => link.value);
  }
}
